import {
  faCheck,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { addMonths, isBefore, parseISO } from 'date-fns';
import { FC, useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Button from 'src/components/0100_button';
import ResponseBox from 'src/components/0100_response_box';
import AugmentedInput from 'src/components/0200_augmented_input';
import BaseSelect from 'src/components/0300_base_select';
import { copyEventSetup } from 'src/graphql/mutations/events.graphql';
import {
  ICopyEventSetupMutation,
  ICopyEventSetupMutationVariables,
} from 'src/graphql/mutations/events.graphql.types';
import { IEventKindEnum } from 'src/graphql/types';
import useButtonStates from 'src/hooks/buttonStates/useButtonStates';
import useEvent from 'src/hooks/events/useEvent';
import useEventAttendees from 'src/hooks/events/useEventAttendees';
import useEvents from 'src/hooks/events/useEvents';
import { useMutation } from 'urql';

const CopySetup: FC = () => {
  const { buttonState } = useButtonStates();
  const { event } = useEvent();
  const { attendees } = useEventAttendees({});
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { events } = useEvents({
    start: addMonths(new Date(), -9),
    end: addMonths(new Date(), 3),
    branchIds: [Number(event?.branch.id)],
    pause: !event?.branch.id,
  });
  const eventsExceptThis = events.filter(
    x => x.kind === IEventKindEnum.Live && x.id !== event?.id,
  );
  const methods = useForm({ defaultValues: { sourceEventId: 0 } });
  const { setValue, watch } = methods;
  const { sourceEventId } = watch();
  const [copyResult, copy] = useMutation<
    ICopyEventSetupMutation,
    ICopyEventSetupMutationVariables
  >(copyEventSetup);
  const handleCopy = useCallback(() => {
    setIsSuccessful(false);
    setError(null);
    copy({
      sourceEventId,
      targetEventId: Number(event?.id),
    }).then(res => {
      if (res.data?.copyEventSetup?.event?.id) {
        setIsSuccessful(true);
        setValue('sourceEventId', 0);
      }

      if (res.data?.copyEventSetup?.error) {
        setError(res.data.copyEventSetup.error);
      }
    });
  }, [copy, event?.id, sourceEventId, setValue]);

  return (
    <FormProvider {...methods}>
      <AugmentedInput title="Copy Tickets + Shifts">
        <div className="grid gap-2 w-full">
          <div className="w-full">
            {attendees.length === 0 ? (
              <BaseSelect
                withSearch
                customSort
                defaultLabel="Select Game to copy from"
                name="sourceEventId"
                selectedValue={sourceEventId}
                width="w-full"
                options={eventsExceptThis
                  .sort((a, b) =>
                    isBefore(parseISO(a.startsAt), parseISO(b.startsAt))
                      ? -1
                      : 1,
                  )
                  .map(x => ({
                    value: x.id,
                    label: x.name,
                  }))}
              />
            ) : (
              <div className="pt-1.5">
                Cannot copy due to Game already having attendances.
              </div>
            )}
          </div>
          {attendees.length === 0 && (
            <div className="grid gap-2">
              <ResponseBox type="neutral" withIcon={faExclamationTriangle}>
                This will overwrite any existing Tickets and Shifts already set
                up for this Game.
              </ResponseBox>
              <div className="flex items-center justify-start gap-2">
                <Button
                  defaultLabel="Execute"
                  state={buttonState({
                    isHighlight: true,
                    isDirty: true,
                    isValid: sourceEventId !== 0,
                    isFetching: copyResult.fetching,
                  })}
                  stateLabel={{ loading: 'Copying...' }}
                  onClick={handleCopy}
                />
                {isSuccessful && (
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faCheck} className="fa-fw" />
                    <div>Copy successful</div>
                  </div>
                )}
              </div>
            </div>
          )}
          {error && <ResponseBox type="error">{error}</ResponseBox>}
        </div>
      </AugmentedInput>
    </FormProvider>
  );
};

export default CopySetup;
